import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import Grid from '@material-ui/core/Grid/Grid';
import Typography from '@material-ui/core/Typography/Typography';
import Button from '@material-ui/core/Button/Button';
import { values } from 'ramda';
import { match } from 'react-router';

import Title from '../shared/Title/Title';
import Layout from '../Layout/Layout';
import { AscendState, getCurrentDebt, getCurrentDebtHistory } from '../../ducks';
import { Debt, PaymentSchedule, Settlement } from '../../models/debt';
import LineChart from '../shared/LineChart';
import { DebtState, getDebtState } from '../../ducks/debt';
import {
  ADD_PAYMENT,
  ADD_SCHEDULE,
  ADD_SETTLEMENT,
  EDIT_DEBT,
  FETCH_DEBT,
} from '../../ducks/actions';
import SettlementOverview from './SettlementOverview/SettlementOverview';
import DebtDetailsForm from './DebtDetailsForm/DebtDetailsForm';
import * as styles from './DebtDetails.css';
import routerHistory from '../../history';
import { formatFieldValue } from '../shared/format';

interface DebtDetailsProps {
  debts: DebtState;
  loading: boolean;
  isMobile: boolean;
  history: object[];
  debt: any;
  addSchedule: (schedule: PaymentSchedule[], debtId: string) => void;
  addPayment: (payment: PaymentSchedule, debtId: string) => void;
  getDebt: (id: number) => void;
  editDebt: (debt: Debt) => void;
  addSettlement: (settlement: Settlement, debtId: number) => void;
  match: match<{ id: number }>;
}

class DebtDetails extends React.Component<DebtDetailsProps> {
  componentDidMount() {
    const { id } = this.props.match.params;
    if (!this.props.debts.data[id]) {
      this.props.getDebt(id);
    }
  }

  public render() {
    const { debt, history, editDebt, isMobile, addSettlement, debts } = this.props;
    const debtData: Debt = debt.data;
    return (
      <Layout>
        <div>
          {debtData && (
            <Title
              text={debtData.name}
              debtOptions={values(debts.data) as any}
              changedDebt={(id) => routerHistory.push(`/debt/${id}`)}
              selectedDebt={debtData}
            />
          )}
          <Grid container justify="space-around">
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              justify="space-around"
              xs={11}
              md={5}
              className={styles.container}>
              <Typography color="primary" variant="headline" className={styles.header}>
                <b>BALANCE</b>
              </Typography>
              {history.length > 0 && (
                <LineChart
                  width="100%"
                  height="70%"
                  data={history}
                  dataKeys={[{ name: debtData.name, color: debt.color }]}
                  isMobile={isMobile}
                />
              )}
            </Grid>
            <Grid
              item
              container
              direction="column"
              alignItems="stretch"
              justify="space-around"
              xs={11}
              md={5}
              className={styles.container}>
              <Grid item container justify="center">
                <Typography color="primary" variant="headline" className={styles.header}>
                  <b>DEBT OVERVIEW</b>
                </Typography>
              </Grid>
              <Grid item container justify="center">
                <Grid item xs={11} sm={10}>
                  <DebtDetailsForm
                    enableReinitialize
                    initialValues={{
                      ...debtData,
                      apr: debtData.apr ? Number(debtData.apr * 100).toFixed(2) : undefined,
                      percentBalanceChange: Number(debtData.percentBalanceChange * 100).toFixed(2),
                    }}
                    onSubmit={(editedDebt: Debt) => {
                      if (editedDebt.apr) {
                        editedDebt.apr = editedDebt.apr * 0.01;
                      }
                      return editDebt(editedDebt);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              container
              direction="column"
              alignItems="center"
              justify={'center'}
              xs={11}
              md={5}
              className={styles.container}>
              <SettlementOverview
                debt={debtData}
                onStateSubmit={(settlement: Settlement) => addSettlement(settlement, debtData.id)}
              />
            </Grid>
            {debtData.negotiatedAmount && (
              <Grid
                item
                container
                direction="column"
                alignItems="center"
                justify="center"
                xs={11}
                md={5}
                className={styles.container}>
                {debtData.schedule && debtData.schedule.length > 0 ? (
                  <Grid item container xs={12}>
                    <Grid item container justify="center">
                      <Typography color="primary" variant="headline" className={styles.header}>
                        <b>SETTLEMENT SCHEDULE</b>
                      </Typography>
                    </Grid>
                    <Grid item container direction="column" justify="center" alignItems="center">
                      <Grid item xs={11} sm={11} container alignItems="center" justify="center">
                        <Grid item xs={12} container className={styles.scheduleHeader}>
                          <Grid item xs={4}>
                            <b>Date</b>
                          </Grid>
                          <Grid item xs={4}>
                            <b>Payment Amount</b>
                          </Grid>
                          <Grid item xs={4}>
                            <b>Amount Paid Off</b>
                          </Grid>
                        </Grid>
                        {debtData.schedule.map((schedule: PaymentSchedule, index) => (
                          <>
                            {index < 6 ? (
                              <Grid key={schedule.id} container className={styles.scheduleRow}>
                                <Grid item xs={4}>
                                  {formatFieldValue(schedule.paymentDueDate, 'date')}
                                </Grid>
                                <Grid item xs={4}>
                                  {formatFieldValue(schedule.paymentAmount, 'number', 'dollar')}
                                </Grid>
                                <Grid item xs={4}>
                                  {formatFieldValue(schedule.paidOff, 'number', 'dollar')}
                                </Grid>
                              </Grid>
                            ) : (
                              <>
                                {index === 6 && (
                                  <Grid key={schedule.id} container className={styles.scheduleRow}>
                                    <Grid item xs={4}>
                                      ...
                                    </Grid>
                                    <Grid item xs={4}>
                                      ...
                                    </Grid>
                                    <Grid item xs={4}>
                                      ...
                                    </Grid>
                                  </Grid>
                                )}
                              </>
                            )}
                          </>
                        ))}
                      </Grid>
                    </Grid>
                    <Grid item container alignItems="center" justify="center">
                      <Button
                        size="large"
                        className={styles.button}
                        variant="contained"
                        color="primary"
                        onClick={() => routerHistory.push(`/debt/${debtData.id}/schedule`)}>
                        Edit Settlement Schedule
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  <Button
                    size="large"
                    className={styles.button}
                    variant="contained"
                    color="primary"
                    onClick={() => routerHistory.push(`/debt/${debtData.id}/schedule`)}>
                    Add Settlement Schedule
                  </Button>
                )}
              </Grid>
            )}
          </Grid>
        </div>
      </Layout>
    );
  }
}

const mapStateToProps = (state: AscendState) => ({
  debts: getDebtState(state),
  debt: getCurrentDebt(state),
  loading: getCurrentDebt(state).loading,
  history: getCurrentDebtHistory(state),
  isMobile: state.layout.isMobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getDebt: (id: number) => dispatch({ type: FETCH_DEBT, id }),
  addSchedule: (schedule: PaymentSchedule[], debtId: number) =>
    dispatch({ type: ADD_SCHEDULE, schedule, debtId }),
  editDebt: (debt: Debt) => dispatch({ type: EDIT_DEBT, debt }),
  addSettlement: (settlement: Settlement, debtId: number) =>
    dispatch({ type: ADD_SETTLEMENT, settlement, debtId }),
  addPayment: (payment: PaymentSchedule, debtId: number) =>
    dispatch({ type: ADD_PAYMENT, payment, debtId }),
});
export default connect<{}, {}, DebtDetailsProps>(mapStateToProps, mapDispatchToProps)(DebtDetails);
